import React, { useCallback, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { recordAmbientListeningEvent } from "./ambientListeningService";

/**
 * Copy pending legal review; recording-consent rules vary by jurisdiction.
 */
export default function AmbientListeningGate({
  open,
  surface,
  encounterRef,
  onResolved,
  onError,
}) {
  const [busy, setBusy] = useState(false);

  const finish = useCallback(
    async (event) => {
      setBusy(true);
      try {
        await recordAmbientListeningEvent({
          surface,
          encounterRef,
          event,
        });
        onResolved(event === "ack_agreed" ? "agreed" : "declined");
      } catch (err) {
        onError(
          "Couldn't record the patient's agreement. Listening is off. Try again.",
        );
      } finally {
        setBusy(false);
      }
    },
    [encounterRef, onError, onResolved, surface],
  );

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>Before the AI listens</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          <Typography variant="body1" paragraph>
            Tell the patient, and anyone with them, that an AI will listen to help
            write notes and suggest possibilities. A clinician reviews everything.
            Did they agree?
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 1 }}>
        <Button
          color="inherit"
          disabled={busy}
          onClick={() => finish("ack_declined")}
        >
          Patient declined
        </Button>
        <LoadingButton
          variant="contained"
          loading={busy}
          onClick={() => finish("ack_agreed")}
        >
          Patient informed &amp; agreed
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

AmbientListeningGate.propTypes = {
  open: PropTypes.bool.isRequired,
  surface: PropTypes.string.isRequired,
  encounterRef: PropTypes.string.isRequired,
  onResolved: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

/**
 * Per-encounter memory gate for ambient listening.
 * @param {{ surface: string, encounterRef: string }} opts
 */
export function useAmbientListeningGate({ surface, encounterRef }) {
  const stateByRef = useRef(new Map());
  const [gateOpen, setGateOpen] = useState(false);
  const [message, setMessage] = useState("");
  const pendingResolve = useRef(null);

  const currentState = encounterRef
    ? stateByRef.current.get(encounterRef) || "unknown"
    : "unknown";

  const requestStart = useCallback(() => {
    setMessage("");
    if (!encounterRef) {
      setMessage(
        "Couldn't record the patient's agreement. Listening is off. Try again.",
      );
      return Promise.resolve(false);
    }
    const state = stateByRef.current.get(encounterRef) || "unknown";
    if (state === "agreed") return Promise.resolve(true);
    if (state === "declined") {
      setMessage("The patient declined AI listening for this encounter.");
      return Promise.resolve(false);
    }
    setGateOpen(true);
    return new Promise((resolve) => {
      pendingResolve.current = resolve;
    });
  }, [encounterRef]);

  const handleResolved = useCallback(
    (next) => {
      if (encounterRef) {
        stateByRef.current.set(encounterRef, next);
      }
      setGateOpen(false);
      if (next === "declined") {
        setMessage("The patient declined AI listening for this encounter.");
      }
      pendingResolve.current?.(next === "agreed");
      pendingResolve.current = null;
    },
    [encounterRef],
  );

  const handleError = useCallback((msg) => {
    setGateOpen(false);
    setMessage(
      msg ||
        "Couldn't record the patient's agreement. Listening is off. Try again.",
    );
    pendingResolve.current?.(false);
    pendingResolve.current = null;
  }, []);

  const logListenStart = useCallback(async () => {
    if (!encounterRef) return;
    await recordAmbientListeningEvent({
      surface,
      encounterRef,
      event: "listen_start",
    });
  }, [encounterRef, surface]);

  const logListenStop = useCallback(async () => {
    if (!encounterRef) return;
    try {
      await recordAmbientListeningEvent({
        surface,
        encounterRef,
        event: "listen_stop",
      });
    } catch (_err) {
      // Best-effort; never block stopping.
    }
  }, [encounterRef, surface]);

  const gateElement = (
    <AmbientListeningGate
      open={gateOpen}
      surface={surface}
      encounterRef={encounterRef || ""}
      onResolved={handleResolved}
      onError={handleError}
    />
  );

  return {
    status: currentState,
    message,
    requestStart,
    logListenStart,
    logListenStop,
    gateElement,
    setMessage,
  };
}
